import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { CharacterCounter } from 'components/CharacterCounter/CharacterCounter';
import { AssetUrls } from 'config/assets';
import { useManageUserEliteMutation } from 'store/services/admin/adminApi';
import './GrantEliteModal.css';

interface GrantEliteModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  username: string;
  onSuccess: () => void;
}

const GrantEliteModal: React.FC<GrantEliteModalProps> = ({
  isOpen,
  onClose,
  userId,
  username,
  onSuccess,
}) => {
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [manageElite, { isLoading }] = useManageUserEliteMutation();

  useEffect(() => {
    if (!isOpen) {
      setExpiresAt('');
      setNote('');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async () => {
    try {
      await manageElite({
        userId,
        body: {
          action: 'upgrade',
          expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
          note: note.trim() || undefined,
        },
      }).unwrap();

      toast.success(`Elite status granted to ${username}.`);
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to grant elite:', error);
      toast.error(error.data?.message || 'Failed to grant elite status. Please try again.');
    }
  };

  const todayStr = new Date().toISOString().split('T')[0];

  const modalContent = (
    <div className="grant-elite-modal-overlay" onClick={onClose}>
      <div className="grant-elite-modal" onClick={(e) => e.stopPropagation()}>
        <div className="grant-elite-modal-header">
          <h2 className="grant-elite-modal-title">Grant Elite to {username}</h2>
          <button className="grant-elite-close-button" onClick={onClose}>
            <img
              src={AssetUrls.icons.dropdowns.close}
              alt="Close"
              style={{ width: 16, height: 16 }}
            />
          </button>
        </div>

        <div className="grant-elite-modal-content">
          <div className="grant-elite-info-banner">
            <img
              src={AssetUrls.icons.elite.badge}
              alt="Elite badge"
              className="grant-elite-banner-badge"
            />
            <span>
              You are granting <strong>Elite status</strong> to{' '}
              <strong className="grant-elite-username">{username}</strong>.
            </span>
          </div>

          <div className="grant-elite-form-group">
            <label htmlFor="expiresAt" className="grant-elite-form-label">
              Expiration Date
              <span className="grant-elite-label-hint"> (leave blank for indefinite)</span>
            </label>
            <input
              id="expiresAt"
              type="date"
              className="grant-elite-form-input grant-elite-date-input"
              value={expiresAt}
              min={todayStr}
              onChange={(e) => setExpiresAt(e.target.value)}
              disabled={isLoading}
            />
            {!expiresAt && (
              <div className="grant-elite-expiry-hint">
                No expiration set — Elite will be active indefinitely until manually revoked.
              </div>
            )}
            {expiresAt && (
              <div className="grant-elite-expiry-hint grant-elite-expiry-hint-active">
                Elite will expire on{' '}
                {new Date(expiresAt + 'T00:00:00').toLocaleDateString('en-US', {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}.
              </div>
            )}
          </div>

          <div className="grant-elite-form-group">
            <label htmlFor="eliteNote" className="grant-elite-form-label">
              Admin Note
              <span className="grant-elite-label-hint"> (optional)</span>
            </label>
            <textarea
              id="eliteNote"
              className="grant-elite-form-textarea"
              placeholder="Reason for manual grant (e.g., Beta tester reward, Community contributor)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={500}
              disabled={isLoading}
            />
            {note.length > 0 && (
              <CharacterCounter
                currentLength={note.length}
                maxLength={500}
                warningThreshold={450}
              />
            )}
          </div>

          <div className="grant-elite-warning-box">
            This grant is separate from Stripe. If the user also has a Stripe subscription,
            both will be active simultaneously.
          </div>
        </div>

        <div className="grant-elite-modal-footer">
          <MagicButton
            label="Cancel"
            color="grey"
            onPress={onClose}
            isDisabled={isLoading}
            fullWidth={false}
          />
          <MagicButton
            label={isLoading ? 'Granting Elite...' : 'Grant Elite'}
            color="purple"
            onPress={handleSubmit}
            isLoading={isLoading}
            isDisabled={isLoading}
            fullWidth={false}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default GrantEliteModal;
